import {
  Button,
  Field,
  Radio,
  Tooltip,
  Typography,
  Icon
} from '@matillion/component-library'
import { type ChangeEvent, useCallback } from 'react'
import { useFormikContext } from 'formik'
import {
  type BringYourOwnGitDetails,
  type GitProvider
} from '../BringYourOwnGitForm/types'
import { type GitProviderSelectorProps } from './types'
import { useTranslation } from 'react-i18next'
import classes from './GitProviderSelector.module.scss'
import classNames from 'classnames'

const GitProviderSelector = ({ className }: GitProviderSelectorProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'byog.form.provider'
  })

  const { handleChange, values, setFieldValue, setFieldTouched } =
    useFormikContext<BringYourOwnGitDetails>()

  const handleValueChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const providerSelection = e.target.value as GitProvider

      if (providerSelection === 'matillion') {
        await setFieldValue('repository', undefined)
        await setFieldTouched('repository', false)
        await setFieldValue('providerParameters', undefined)
        await setFieldTouched('providerParameters', false)
      }

      await setFieldValue('provider', providerSelection).then(() => {
        handleChange(e)
      })
    },
    [handleChange, setFieldTouched, setFieldValue]
  )

  return (
    <div
      data-testid="git-provider-form"
      className={classNames(className, classes.form)}
    >
      <div className={classes.titleWrapper}>
        <Typography
          format="bcs"
          className={classes.title}
          data-testid="git-provider-title"
          weight="bold"
        >
          {t('title')}
        </Typography>

        <Tooltip content={t('tooltip')} className={classes.tooltip}>
          <Button
            alt="ghost"
            className={classes.tooltipButton}
            data-testid="git-provider-tooltip-button"
          >
            <Icon.InfoNoBg />
          </Button>
        </Tooltip>
      </div>

      <Typography
        format="bcs"
        className={classes.subtitle}
        data-testid="git-provider-subtitle"
      >
        {t('subtitle')}
      </Typography>

      <Field
        name="provider"
        value="matillion"
        text={t('matillion')}
        inputComponent={Radio}
        onChange={handleValueChange}
        className={classes.matillion}
        data-testid="git-provider-matillion"
        checked={values.provider === 'matillion'}
        labelDataTestId="git-provider-matillion-input"
      />
      <Field
        value="github"
        name="provider"
        text={t('github')}
        inputComponent={Radio}
        className={classes.github}
        onChange={handleValueChange}
        data-testid="git-provider-github"
        checked={values.provider === 'github'}
        labelDataTestId="git-provider-github-input"
      />

      <div className={classes.gitHubAppsHelp}>
        <Typography
          format="bcs"
          className={classes.helpText}
          data-testid="git-provider-help-text"
        >
          {t('github-help')}
        </Typography>

        <span> </span>

        <Typography format="bcs">
          <a
            target="_blank"
            rel="noreferrer"
            className={classes.helpLink}
            href="https://support.matillion.com"
            data-testid="git-provider-help-link"
          >
            {t('github-help-link')}
            <Icon.Export className={classes.exportIcon} />
          </a>
        </Typography>
      </div>
    </div>
  )
}

export default GitProviderSelector
